import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AdminAuthCard from "@/components/admin/AdminAuthCard";
import JeaButton from "@/components/jea/JeaButton";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

const AdminTwoFactorPage = () => {
  const location = useLocation();
  const nav = useNavigate();
  const { adminVerify2FA, isAuthenticated, isAdmin } = useAuth();
  const pending_token = location.state?.pending_token || "";
  const email = location.state?.email || "";
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputs = useRef([]);

  useEffect(() => { inputs.current[0]?.focus(); }, []);

  if (isAuthenticated && isAdmin) return <Navigate to="/admin" replace />;
  if (!pending_token) return <Navigate to="/admin/login" replace />;

  const setDigit = (idx, val) => {
    const clean = (val || "").replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev]; next[idx] = clean; return next;
    });
    if (clean && idx < 5) inputs.current[idx + 1]?.focus();
  };
  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) inputs.current[idx - 1]?.focus();
    if (e.key === "ArrowLeft" && idx > 0) inputs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) inputs.current[idx + 1]?.focus();
  };
  const onPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const arr = ["","","","","",""];
    for (let i = 0; i < text.length; i++) arr[i] = text[i];
    setDigits(arr);
    inputs.current[Math.min(text.length, 5)]?.focus();
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const code = digits.join("");
    if (code.length !== 6) { setError("Enter all 6 digits."); return; }
    setError("");
    setLoading(true);
    try {
      await adminVerify2FA(pending_token, code);
      toast.success("Signed in as admin.");
      nav("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminAuthCard>
      <form onSubmit={submit} className="space-y-6" data-testid="admin-twofactor-form">
        <div className="text-center space-y-2">
          <h2 className="text-[20px] font-bold text-white">Verify Admin Access</h2>
          <p className="text-[14px] text-text-secondary">
            Enter the 6-digit code.
            <br />
            <span className="text-text-muted text-[12px]">
              (Demo mode: any 6 digits will work{email ? ` for ${email}` : ""}.)
            </span>
          </p>
        </div>

        <div className="flex justify-between gap-2" onPaste={onPaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              inputMode="numeric"
              maxLength={1}
              autoComplete="one-time-code"
              data-testid={`admin-twofactor-digit-${i}`}
              className={cn(
                "h-14 w-12 text-center text-[20px] font-bold text-white bg-bg-tertiary rounded-[8px] border transition-all duration-150",
                d ? "border-danger" : "border-border-default",
                "focus:outline-none focus:border-danger focus:shadow-[0_0_0_4px_rgba(231,76,60,0.30)]"
              )}
            />
          ))}
        </div>

        {error && (
          <div
            className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2"
            data-testid="admin-twofactor-error"
          >
            {error}
          </div>
        )}

        <JeaButton
          type="submit" size="lg" fullWidth loading={loading}
          className="!bg-danger !text-white hover:!bg-danger/90"
          data-testid="admin-twofactor-verify"
        >
          Verify
        </JeaButton>

        <div className="text-center">
          <Link
            to="/admin/login"
            className="text-[12px] text-text-muted hover:text-accent-pink transition-colors"
            data-testid="admin-twofactor-back"
          >
            Back to admin login
          </Link>
        </div>
      </form>
    </AdminAuthCard>
  );
};

export default AdminTwoFactorPage;
